import type { ReactNode } from 'react';

import { DropdownMenu } from 'radix-ui';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { LOCALES, LOCALE_NAMES, setLocale, toApiLanguage, useLocale } from '@/shared/i18n';
import type { Locale } from '@/shared/i18n';
import { api } from '@/shared/services/api/endpoints';
import { cn } from '@/shared/ui/cn';
import type { IconName } from '@/shared/ui/Icon';
import { Icon } from '@/shared/ui/Icon';

import { useInstallPrompt } from '@/app/shell/useInstallPrompt';
import { SPACE_ROOTS, useAppMode, useMe, useSessionStore } from '@/stores/session.store';
import type { ThemePreference } from '@/stores/theme.store';
import { useThemePreference, useThemeStore } from '@/stores/theme.store';

const THEMES: ReadonlyArray<{ key: ThemePreference; icon: IconName; label: 'web:theme.system' | 'web:theme.light' | 'web:theme.dark' }> = [
  { key: 'system', icon: 'monitor', label: 'web:theme.system' },
  { key: 'light', icon: 'sun', label: 'web:theme.light' },
  { key: 'dark', icon: 'moon', label: 'web:theme.dark' },
];

const itemClasses =
  'flex cursor-pointer items-center gap-3 rounded-sm px-3 py-2.5 text-subtle text-fg outline-none select-none data-[highlighted]:bg-fill';

/**
 * Le menu « Plus » de la barre latérale : l'apparence, la langue, le passage
 * d'un espace à l'autre, les réglages et la déconnexion. Sur téléphone, tout
 * ceci vit dans « Mon espace ».
 */
export function MoreMenu({ trigger }: { trigger: ReactNode }) {
  const { t } = useTranslation(['common', 'web', 'account']);
  const navigate = useNavigate();
  const me = useMe();
  const mode = useAppMode();
  const locale = useLocale();
  const theme = useThemePreference();
  const setTheme = useThemeStore((state) => state.setPreference);
  const setMode = useSessionStore((state) => state.setMode);
  const signOut = useSessionStore((state) => state.signOut);
  const { install } = useInstallPrompt();

  const canSell = me ? me.ownedShopIds.length > 0 || me.memberShopIds.length > 0 : false;

  const changeLocale = (next: Locale) => {
    void setLocale(next);
    // Le serveur retient la langue du compte : un téléphone neuf la retrouvera.
    void api.users.updateProfile({ language: toApiLanguage(next) }).catch(() => undefined);
  };

  const switchSpace = (next: 'buyer' | 'seller') => {
    setMode(next);
    void navigate(SPACE_ROOTS[next]);
  };

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>{trigger}</DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          side="top"
          align="start"
          sideOffset={8}
          className="z-50 w-[268px] animate-scale-in rounded-md border border-line bg-raised p-1.5 shadow-popover"
        >
          {canSell && (mode === 'buyer' || mode === 'seller') ? (
            <>
              <DropdownMenu.Item
                className={itemClasses}
                onSelect={() => switchSpace(mode === 'buyer' ? 'seller' : 'buyer')}
              >
                <Icon name={mode === 'buyer' ? 'chart' : 'store'} size={19} className="text-fg-strong" />
                {t(mode === 'buyer' ? 'web:shell.switchToSeller' : 'web:shell.switchToBuyer')}
              </DropdownMenu.Item>
              <DropdownMenu.Separator className="my-1 h-px bg-line-soft" />
            </>
          ) : null}

          <DropdownMenu.Label className="px-3 pt-2 pb-1 text-caption text-fg-muted">{t('web:theme.title')}</DropdownMenu.Label>
          <DropdownMenu.RadioGroup value={theme} onValueChange={(value) => setTheme(value as ThemePreference)}>
            {THEMES.map((option) => (
              <DropdownMenu.RadioItem key={option.key} value={option.key} className={itemClasses}>
                <Icon name={option.icon} size={19} className="text-fg-strong" />
                <span className="flex-1">{t(option.label)}</span>
                <DropdownMenu.ItemIndicator>
                  <Icon name="check" size={16} className="text-accent-text" />
                </DropdownMenu.ItemIndicator>
              </DropdownMenu.RadioItem>
            ))}
          </DropdownMenu.RadioGroup>

          <DropdownMenu.Separator className="my-1 h-px bg-line-soft" />
          <DropdownMenu.Label className="px-3 pt-2 pb-1 text-caption text-fg-muted">
            <Icon name="globe" size={14} className="mr-1.5 inline align-[-2px]" />
            {LOCALE_NAMES[locale]}
          </DropdownMenu.Label>
          <DropdownMenu.RadioGroup value={locale} onValueChange={(value) => changeLocale(value as Locale)}>
            {LOCALES.map((option) => (
              <DropdownMenu.RadioItem key={option} value={option} className={itemClasses}>
                <span className="flex-1" lang={option}>
                  {LOCALE_NAMES[option]}
                </span>
                <DropdownMenu.ItemIndicator>
                  <Icon name="check" size={16} className="text-accent-text" />
                </DropdownMenu.ItemIndicator>
              </DropdownMenu.RadioItem>
            ))}
          </DropdownMenu.RadioGroup>

          <DropdownMenu.Separator className="my-1 h-px bg-line-soft" />
          {install ? (
            <DropdownMenu.Item className={itemClasses} onSelect={() => void install()}>
              <Icon name="download" size={19} className="text-fg-strong" />
              {t('web:install.action')}
            </DropdownMenu.Item>
          ) : null}
          <DropdownMenu.Item className={itemClasses} onSelect={() => void navigate('/settings')}>
            <Icon name="settings" size={19} className="text-fg-strong" />
            {t('account:menu.settings')}
          </DropdownMenu.Item>
          <DropdownMenu.Item
            className={cn(itemClasses, 'text-danger')}
            onSelect={() => {
              void signOut().then(() => navigate('/welcome', { replace: true }));
            }}
          >
            <Icon name="exit" size={19} className="text-danger" />
            {t('account:menu.signOut')}
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
